import { useMemo, type CSSProperties } from 'react';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    BarChart,
    Bar,
} from 'recharts';
import type { DashboardFiltros } from './types';

type UsuarioTempoUso = {
    usuario: string;
    tempoTotalMin: number;
    mediaPorDiaMin: number;
    sessoes: number;
    ultimoLogin: string;
    ativoHoje: boolean;
    tempoMaquinaOperouMin: number;
    conciliacoesHumanoDia: number;
    conciliacoesMaquinaDia: number;
    salarioAnalista: number;
    cargaTributariaMultiplicador: number;
};

type UsoDiario = {
    dia: string;
    Fernando: number;
    Lorena: number;
    Gustavo: number;
    Marcia: number;
};

type Props = {
    filtros: DashboardFiltros;
};

const DashboardTempoUso = ({ filtros }: Props) => {
    const cores = {
        laranja: '#f59e0b',
        verde: '#14b8a6',
        azul: '#2563eb',
        azulEscuro: '#1d4ed8',
        cinzaBorda: '#e9ecef',
        cinzaTexto: '#5f6368',
        fundoCard: '#ffffff',
        titulo: '#3c4043',
        fundoSuave: '#f8f9fb',
        verdeSuave: '#e9f7f1',
        azulSuave: '#eef4ff',
        laranjaSuave: '#fff4e5',
        roxoSuave: '#f3e8ff',
        verdeClaro: '#ecfdf3',
    };

    const usuarios = useMemo<UsuarioTempoUso[]>(() => {
        return [
            {
                usuario: 'Fernando Lopes',
                tempoTotalMin: 1130,
                mediaPorDiaMin: 188,
                sessoes: 20,
                ultimoLogin: 'Hoje às 09:45',
                ativoHoje: true,
                tempoMaquinaOperouMin: 510,
                conciliacoesHumanoDia: 100000,
                conciliacoesMaquinaDia: 800000,
                salarioAnalista: 2000,
                cargaTributariaMultiplicador: 1.68,
            },
            {
                usuario: 'Lorena Lima',
                tempoTotalMin: 980,
                mediaPorDiaMin: 163,
                sessoes: 17,
                ultimoLogin: 'Hoje às 09:20',
                ativoHoje: true,
                tempoMaquinaOperouMin: 440,
                conciliacoesHumanoDia: 100000,
                conciliacoesMaquinaDia: 620000,
                salarioAnalista: 2000,
                cargaTributariaMultiplicador: 1.68,
            },
            {
                usuario: 'Gustavo Pereira',
                tempoTotalMin: 935,
                mediaPorDiaMin: 155,
                sessoes: 15,
                ultimoLogin: 'Hoje às 11:15',
                ativoHoje: true,
                tempoMaquinaOperouMin: 395,
                conciliacoesHumanoDia: 100000,
                conciliacoesMaquinaDia: 540000,
                salarioAnalista: 2000,
                cargaTributariaMultiplicador: 1.68,
            },
            {
                usuario: 'Marcia Costa',
                tempoTotalMin: 910,
                mediaPorDiaMin: 151,
                sessoes: 13,
                ultimoLogin: 'Ontem às 18:40',
                ativoHoje: false,
                tempoMaquinaOperouMin: 360,
                conciliacoesHumanoDia: 100000,
                conciliacoesMaquinaDia: 470000,
                salarioAnalista: 2000,
                cargaTributariaMultiplicador: 1.68,
            },
        ];
    }, [filtros]);

    const usoDiario = useMemo<UsoDiario[]>(() => {
        return [
            { dia: 'Dom', Fernando: 3, Lorena: 6, Gustavo: 4, Marcia: 2 },
            { dia: 'Seg', Fernando: 4, Lorena: 5, Gustavo: 4, Marcia: 3 },
            { dia: 'Ter', Fernando: 5, Lorena: 8, Gustavo: 5, Marcia: 4 },
            { dia: 'Qua', Fernando: 5, Lorena: 8, Gustavo: 5, Marcia: 4 },
            { dia: 'Qui', Fernando: 6, Lorena: 11, Gustavo: 7, Marcia: 5 },
            { dia: 'Sex', Fernando: 6, Lorena: 11, Gustavo: 7, Marcia: 5 },
            { dia: 'Sáb', Fernando: 5, Lorena: 12, Gustavo: 8, Marcia: 3 },
        ];
    }, [filtros]);

    const totalTempoMin = useMemo(() => {
        return usuarios.reduce((acc, item) => acc + item.tempoTotalMin, 0);
    }, [usuarios]);

    const mediaPorUsuarioMin = useMemo(() => {
        if (!usuarios.length) return 0;
        return Math.round(totalTempoMin / usuarios.length);
    }, [usuarios, totalTempoMin]);

    const totalSessoes = useMemo(() => {
        return usuarios.reduce((acc, item) => acc + item.sessoes, 0);
    }, [usuarios]);

    const mediaSessoes = useMemo(() => {
        if (!usuarios.length) return 0;
        return Math.round(totalSessoes / usuarios.length);
    }, [usuarios, totalSessoes]);

    const formatarTempo = (minutos: number) => {
        const horas = Math.floor(minutos / 60);
        const mins = minutos % 60;
        return `${horas}h ${mins.toString().padStart(2, '0')}min`;
    };

    const formatarMoeda = (valor: number) => {
        return valor.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        });
    };

    const calcularCustoMensalTotalAnalista = (usuario: UsuarioTempoUso) => {
        return usuario.salarioAnalista * usuario.cargaTributariaMultiplicador;
    };

    const calcularCustoDiarioAnalista = (usuario: UsuarioTempoUso) => {
        const custoMensalTotal = calcularCustoMensalTotalAnalista(usuario);
        return custoMensalTotal / 22;
    };

    const calcularEquivalenciaHumana = (usuario: UsuarioTempoUso) => {
        if (!usuario.conciliacoesHumanoDia) return 0;
        return usuario.conciliacoesMaquinaDia / usuario.conciliacoesHumanoDia;
    };

    const calcularEconomiaDiaria = (usuario: UsuarioTempoUso) => {
        const custoDiarioAnalista = calcularCustoDiarioAnalista(usuario);
        const equivalenciaHumana = calcularEquivalenciaHumana(usuario);

        return custoDiarioAnalista * equivalenciaHumana;
    };

    const calcularEconomiaMensal = (usuario: UsuarioTempoUso) => {
        return calcularEconomiaDiaria(usuario) * 22;
    };

    const rankingUsuarios = useMemo(() => {
        return [...usuarios]
            .map((usuario) => ({
                ...usuario,
                custoMensalTotalAnalista: calcularCustoMensalTotalAnalista(usuario),
                custoDiarioAnalista: calcularCustoDiarioAnalista(usuario),
                equivalenciaHumana: calcularEquivalenciaHumana(usuario),
                economiaDiaria: calcularEconomiaDiaria(usuario),
                economiaMensal: calcularEconomiaMensal(usuario),
            }))
            .sort((a, b) => b.economiaMensal - a.economiaMensal);
    }, [usuarios]);

    const totalTempoMaquinaOperouMin = useMemo(() => {
        return usuarios.reduce((acc, item) => acc + item.tempoMaquinaOperouMin, 0);
    }, [usuarios]);

    const totalEconomiaMensal = useMemo(() => {
        return rankingUsuarios.reduce((acc, item) => acc + item.economiaMensal, 0);
    }, [rankingUsuarios]);

    const totalConciliacoesMaquinaDia = useMemo(() => {
        return usuarios.reduce((acc, item) => acc + item.conciliacoesMaquinaDia, 0);
    }, [usuarios]);

    const totalEquivalenciaHumana = useMemo(() => {
        return rankingUsuarios.reduce((acc, item) => acc + item.equivalenciaHumana, 0);
    }, [rankingUsuarios]);

    const cardStyle: CSSProperties = {
        background: cores.fundoCard,
        border: `1px solid ${cores.cinzaBorda}`,
        borderRadius: '14px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        padding: '14px 16px',
        height: '100%',
    };

    const tituloSecao: CSSProperties = {
        fontSize: '18px',
        fontWeight: 700,
        color: cores.titulo,
        marginBottom: '14px',
    };

    const subtitulo: CSSProperties = {
        fontSize: '15px',
        fontWeight: 700,
        color: cores.titulo,
        marginBottom: '12px',
    };

    const kpiBase = (background: string): CSSProperties => ({
        borderRadius: '14px',
        padding: '16px',
        color: '#1f2937',
        background,
        border: `1px solid ${cores.cinzaBorda}`,
        boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
        height: '100%',
    });

    return (
        <div className='row g-5'>
            <div className='col-12'>
                <div style={cardStyle}>
                    <div style={tituloSecao}>Dashboard de Tempo de Uso por Usuários</div>

                    <div className='row g-4 mb-2'>
                        <div className='col-sm-6 col-lg-2'>
                            <div style={kpiBase(cores.laranjaSuave)}>
                                <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '10px' }}>
                                    Tempo Total Ativo
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 800, color: '#d97706', lineHeight: 1 }}>
                                    {formatarTempo(totalTempoMin)}
                                </div>
                                <div style={{ fontSize: '13px', color: cores.cinzaTexto, marginTop: '8px' }}>
                                    Tempo total somado no período
                                </div>
                            </div>
                        </div>

                        <div className='col-sm-6 col-lg-2'>
                            <div style={kpiBase(cores.verdeSuave)}>
                                <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '10px' }}>
                                    Média por Usuário
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 800, color: '#0f766e', lineHeight: 1 }}>
                                    {formatarTempo(mediaPorUsuarioMin)}
                                </div>
                                <div style={{ fontSize: '13px', color: cores.cinzaTexto, marginTop: '8px' }}>
                                    Média no período selecionado
                                </div>
                            </div>
                        </div>

                        <div className='col-sm-6 col-lg-2'>
                            <div style={kpiBase(cores.azulSuave)}>
                                <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '10px' }}>
                                    Sessões por Usuário
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 800, color: cores.azul, lineHeight: 1 }}>
                                    {mediaSessoes}
                                </div>
                                <div style={{ fontSize: '13px', color: cores.cinzaTexto, marginTop: '8px' }}>
                                    Média de sessões realizadas
                                </div>
                            </div>
                        </div>

                        <div className='col-sm-6 col-lg-2'>
                            <div style={kpiBase('#eef2ff')}>
                                <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '10px' }}>
                                    Usuários Ativos
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 800, color: cores.azulEscuro, lineHeight: 1 }}>
                                    {usuarios.filter((u) => u.ativoHoje).length}/{usuarios.length}
                                </div>
                                <div style={{ fontSize: '13px', color: cores.cinzaTexto, marginTop: '8px' }}>
                                    Usuários com login recente
                                </div>
                            </div>
                        </div>

                        <div className='col-sm-6 col-lg-2'>
                            <div style={kpiBase(cores.roxoSuave)}>
                                <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '10px' }}>
                                    Tempo Máquina Operou
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 800, color: '#7c3aed', lineHeight: 1 }}>
                                    {formatarTempo(totalTempoMaquinaOperouMin)}
                                </div>
                                <div style={{ fontSize: '13px', color: cores.cinzaTexto, marginTop: '8px' }}>
                                    Processamento automático no período
                                </div>
                            </div>
                        </div>

                        <div className='col-sm-6 col-lg-2'>
                            <div style={kpiBase(cores.verdeClaro)}>
                                <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '10px' }}>
                                    Total Economizado
                                </div>
                                <div
                                    style={{
                                        fontSize: '24px',
                                        fontWeight: 800,
                                        color: '#15803d',
                                        lineHeight: 1.1,
                                    }}
                                >
                                    {formatarMoeda(totalEconomiaMensal)}
                                </div>
                                <div style={{ fontSize: '13px', color: cores.cinzaTexto, marginTop: '8px' }}>
                                    Economia mensal estimada
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='col-xl-4 col-12'>
                <div style={cardStyle}>
                    <div style={subtitulo}>Detalhamento por Usuário</div>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                        }}
                    >
                        {rankingUsuarios.map((usuario, index) => (
                            <div
                                key={index}
                                style={{
                                    border: '1px solid #eef0f3',
                                    borderRadius: '12px',
                                    padding: '12px',
                                    background: '#fafbfd',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: '12px',
                                    }}
                                >
                                    <div>
                                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#2c2f33' }}>
                                            {usuario.usuario}
                                        </div>
                                        <div style={{ fontSize: '12px', color: cores.cinzaTexto, marginTop: '4px' }}>
                                            Último login: {usuario.ultimoLogin}
                                        </div>
                                    </div>

                                    <span
                                        style={{
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            padding: '4px 8px',
                                            borderRadius: '999px',
                                            background: usuario.ativoHoje ? '#e9f7ef' : '#fff1f2',
                                            color: usuario.ativoHoje ? '#0f9f61' : '#d32f2f',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {usuario.ativoHoje ? 'Ativo' : 'Inativo'}
                                    </span>
                                </div>

                                <div className='row mt-3'>
                                    <div className='col-6'>
                                        <div style={{ fontSize: '12px', color: cores.cinzaTexto }}>Tempo total</div>
                                        <div style={{ fontSize: '14px', fontWeight: 700 }}>
                                            {formatarTempo(usuario.tempoTotalMin)}
                                        </div>
                                    </div>
                                    <div className='col-6'>
                                        <div style={{ fontSize: '12px', color: cores.cinzaTexto }}>Média por dia</div>
                                        <div style={{ fontSize: '14px', fontWeight: 700 }}>
                                            {formatarTempo(usuario.mediaPorDiaMin)}
                                        </div>
                                    </div>
                                </div>

                                <div className='row mt-3'>
                                    <div className='col-6'>
                                        <div style={{ fontSize: '12px', color: cores.cinzaTexto }}>Tempo máquina</div>
                                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#7c3aed' }}>
                                            {formatarTempo(usuario.tempoMaquinaOperouMin)}
                                        </div>
                                    </div>
                                    <div className='col-6'>
                                        <div style={{ fontSize: '12px', color: cores.cinzaTexto }}>Economia mensal</div>
                                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#15803d' }}>
                                            {formatarMoeda(usuario.economiaMensal)}
                                        </div>
                                    </div>
                                </div>

                                <div className='mt-3'>
                                    <div style={{ fontSize: '12px', color: cores.cinzaTexto, marginBottom: '6px' }}>
                                        Sessões realizadas
                                    </div>
                                    <div style={{ fontSize: '14px', fontWeight: 700 }}>{usuario.sessoes}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className='col-xl-8 col-12'>
                <div className='row g-5'>
                    <div className='col-12'>
                        <div style={cardStyle}>
                            <div style={subtitulo}>Distribuição Diária de Uso por Usuário</div>

                            <div style={{ width: '100%', height: 320 }}>
                                <ResponsiveContainer width='100%' height='100%'>
                                    <LineChart data={usoDiario} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray='3 3' stroke='#f1f3f4' />
                                        <XAxis dataKey='dia' tick={{ fontSize: 12, fill: '#4b4f52' }} />
                                        <YAxis tick={{ fontSize: 12, fill: '#4b4f52' }} />
                                        <Tooltip />
                                        <Line type='monotone' dataKey='Fernando' stroke={cores.azul} strokeWidth={3} dot={{ r: 4 }} />
                                        <Line type='monotone' dataKey='Lorena' stroke={cores.laranja} strokeWidth={3} dot={{ r: 4 }} />
                                        <Line type='monotone' dataKey='Gustavo' stroke={cores.verde} strokeWidth={3} dot={{ r: 4 }} />
                                        <Line type='monotone' dataKey='Marcia' stroke={cores.azulEscuro} strokeWidth={3} dot={{ r: 4 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    <div className='col-12'>
                        <div style={cardStyle}>
                            <div style={subtitulo}>Capacidade da Máquina x Equivalência Humana</div>

                            <div className='row g-4 mb-4'>
                                <div className='col-md-4'>
                                    <div
                                        style={{
                                            background: '#fafbfd',
                                            border: '1px solid #eef0f3',
                                            borderRadius: '12px',
                                            padding: '14px',
                                        }}
                                    >
                                        <div style={{ fontSize: '12px', color: cores.cinzaTexto, marginBottom: '6px' }}>
                                            Conciliações pela máquina / dia
                                        </div>
                                        <div style={{ fontSize: '24px', fontWeight: 800, color: cores.azul }}>
                                            {totalConciliacoesMaquinaDia.toLocaleString('pt-BR')}
                                        </div>
                                    </div>
                                </div>

                                <div className='col-md-4'>
                                    <div
                                        style={{
                                            background: '#fafbfd',
                                            border: '1px solid #eef0f3',
                                            borderRadius: '12px',
                                            padding: '14px',
                                        }}
                                    >
                                        <div style={{ fontSize: '12px', color: cores.cinzaTexto, marginBottom: '6px' }}>
                                            Equivalente humano estimado
                                        </div>
                                        <div style={{ fontSize: '24px', fontWeight: 800, color: '#7c3aed' }}>
                                            {totalEquivalenciaHumana.toFixed(1)} analistas
                                        </div>
                                    </div>
                                </div>

                                <div className='col-md-4'>
                                    <div
                                        style={{
                                            background: '#fafbfd',
                                            border: '1px solid #eef0f3',
                                            borderRadius: '12px',
                                            padding: '14px',
                                        }}
                                    >
                                        <div style={{ fontSize: '12px', color: cores.cinzaTexto, marginBottom: '6px' }}>
                                            Economia mensal estimada
                                        </div>
                                        <div style={{ fontSize: '24px', fontWeight: 800, color: '#15803d' }}>
                                            {formatarMoeda(totalEconomiaMensal)}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div style={{ width: '100%', height: 280 }}>
                                <ResponsiveContainer width='100%' height='100%'>
                                    <BarChart
                                        data={rankingUsuarios.map((usuario) => ({
                                            usuario: usuario.usuario,
                                            equivalenteHumano: Number(usuario.equivalenciaHumana.toFixed(2)),
                                        }))}
                                        layout='vertical'
                                        margin={{ top: 10, right: 20, left: 10, bottom: 0 }}
                                        barCategoryGap={14}
                                    >
                                        <CartesianGrid strokeDasharray='3 3' horizontal={false} stroke='#f1f3f4' />
                                        <XAxis type='number' tick={{ fontSize: 12, fill: '#4b4f52' }} />
                                        <YAxis
                                            type='category'
                                            dataKey='usuario'
                                            width={120}
                                            tick={{ fontSize: 12, fill: '#4b4f52' }}
                                        />
                                        <Tooltip
                                            formatter={(value: number) => [`${value} analistas`, 'Equivalência humana']}
                                        />
                                        <Bar dataKey='equivalenteHumano' fill={cores.azul} radius={[0, 6, 6, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='col-12'>
                <div style={cardStyle}>
                    <div style={subtitulo}>Resumo Estratégico de Adoção</div>

                    <div className='table-responsive'>
                        <table className='table align-middle table-row-dashed fs-6 gy-5'>
                            <thead>
                                <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                                    <th>Usuário</th>
                                    <th>Tempo Total Ativo</th>
                                    <th>Média por Dia</th>
                                    <th>Tempo Máquina Operou</th>
                                    <th>Conciliações Máquina/Dia</th>
                                    <th>Total Economizado</th>
                                    <th>Sessões</th>
                                    <th>Último Login</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody className='fw-semibold text-gray-600'>
                                {rankingUsuarios.map((usuario, index) => (
                                    <tr key={index}>
                                        <td>{usuario.usuario}</td>
                                        <td>{formatarTempo(usuario.tempoTotalMin)}</td>
                                        <td>{formatarTempo(usuario.mediaPorDiaMin)}</td>
                                        <td>{formatarTempo(usuario.tempoMaquinaOperouMin)}</td>
                                        <td>{usuario.conciliacoesMaquinaDia.toLocaleString('pt-BR')}</td>
                                        <td style={{ color: '#15803d', fontWeight: 700 }}>
                                            {formatarMoeda(usuario.economiaMensal)}
                                        </td>
                                        <td>{usuario.sessoes}</td>
                                        <td>{usuario.ultimoLogin}</td>
                                        <td>
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    padding: '4px 10px',
                                                    borderRadius: '999px',
                                                    fontSize: '12px',
                                                    fontWeight: 700,
                                                    background: usuario.ativoHoje ? '#e9f7ef' : '#fff1f2',
                                                    color: usuario.ativoHoje ? '#0f9f61' : '#d32f2f',
                                                }}
                                            >
                                                {usuario.ativoHoje ? 'Em uso' : 'Reengajar'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div
                        style={{
                            marginTop: '10px',
                            fontSize: '13px',
                            color: cores.cinzaTexto,
                            lineHeight: 1.6,
                        }}
                    >
                        O cálculo de economia está em hardcode no frontend e considera a lógica visual: salário base do analista
                        multiplicado por 1,68 de carga tributária para chegar ao custo mensal total, dividido por 22 dias úteis
                        para encontrar o custo diário, e multiplicado pela equivalência entre a quantidade de conciliações
                        realizadas pela máquina por dia versus a produtividade humana média no mesmo período.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardTempoUso;